import { useEffect, useMemo, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as updatedProductStore from '@cartwhl/common/stores/updatedProductStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getTrialActivationChargeSummary } from '@cartwhl/common/utils/getTrialActivationChargeSummary';
import {
  isActiveFreeTrial,
  isOrgCreditsWorkspace,
} from '@cartwhl/common/utils/organizationWorkspace';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

export default function ActivateEarlyTrialModal({
  close,
}: {
  close: () => void;
}) {
  const { done: updatedCatalogReady } = useSnapshot(updatedProductStore.state);
  const { organizationInfo } = useSnapshot(userStore.state);
  const [payNowLoading, setPayNowLoading] = useState(false);

  const trialActivationSummary = useMemo(() => {
    if (!updatedCatalogReady || !organizationInfo) {
      return null;
    }
    if (
      !isOrgCreditsWorkspace(organizationInfo) ||
      !isActiveFreeTrial(organizationInfo)
    ) {
      return null;
    }
    return getTrialActivationChargeSummary(organizationInfo);
  }, [organizationInfo, updatedCatalogReady]);

  useEffect(() => {
    if (!organizationInfo || !isOrgCreditsWorkspace(organizationInfo)) {
      return;
    }
    if (!isActiveFreeTrial(organizationInfo)) {
      return;
    }
    void updatedProductStore.fetchProducts();
  }, [organizationInfo]);

  function handleOpenChange(open: boolean) {
    if (!open) {
      close();
    }
  }

  return (
    <Modal
      title="Activate now to unlock credits"
      maxWidth={440}
      open
      onOpenChange={handleOpenChange}
      footer={
        <div className="mt-6 flex flex-row flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={payNowLoading}
              onClick={close}
            >
              Not now
            </Button>
            <Button
              variant="secondary"
              disabled={payNowLoading}
              onClick={() => {
                const id = organizationInfo?.organizationID?.trim();
                if (id) {
                  updatedProductStore.snoozeActivateEarlyTrialAutoloadForTwoHours(
                    id,
                  );
                } else {
                  close();
                }
              }}
            >
              Snooze for 2 hours
            </Button>
          </div>
          <Button
            className="ml-auto"
            variant="primary"
            loading={payNowLoading}
            onClick={async () => {
              if (payNowLoading) return;
              setPayNowLoading(true);
              try {
                const ok = await updatedProductStore.completeStripeTrialEarly();
                if (ok) {
                  close();
                }
              } finally {
                setPayNowLoading(false);
              }
            }}
          >
            I agree, activate
          </Button>
        </div>
      }
    >
      {trialActivationSummary ? (
        <div className="text-primary flex flex-col gap-2">
          <Text
            as="p"
            variant="studio/body/xs"
            className="text-primary leading-snug"
          >
            {`By activating your plan, you'll unlock all monthly credits and your account will be billed for the following:`}
          </Text>
          <div className="flex flex-col gap-1.5">
            {(
              [
                {
                  label: 'Plan type',
                  value: trialActivationSummary.planLabel,
                },
                {
                  label: 'Seats',
                  value: String(trialActivationSummary.seatCount),
                },
                {
                  label: 'Billing type',
                  value: trialActivationSummary.billingTypeLabel,
                },
                {
                  label: 'Total cost',
                  value: trialActivationSummary.totalLabel,
                },
              ] as const
            ).map((row, index, rows) => {
              const isTotalRow = index === rows.length - 1;
              return (
                <div
                  key={row.label}
                  className={`flex items-start justify-between gap-4 ${
                    isTotalRow ? 'border-primary border-t pt-2' : ''
                  }`}
                >
                  <Text
                    variant="studio/body/sm"
                    className="text-secondary shrink-0"
                  >
                    {row.label}
                  </Text>
                  <Text
                    variant="studio/body/sm"
                    className={`min-w-0 flex-1 text-right ${
                      isTotalRow ? 'font-semibold' : 'font-medium'
                    }`}
                  >
                    {row.value}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <Text as="p" variant="studio/body/sm" className="text-tertiary">
          By activating now, you agree to be charged at your current plan’s
          rate. Your card will be charged when you continue.
        </Text>
      )}
    </Modal>
  );
}
